import { BindAll } from 'lodash-decorators'
import type { MockGuideClasshourDetailType, WaterfallMoment } from '@xt/client/entities/course-v2/types.d'
import $global from '@xt/client/store/global'
import { action, observable } from 'mobx'
import { CommentItem } from './comments'
import { Content } from './user-content'
import { type DriveStep } from 'driver.js'
import moment from 'moment'
import { $service } from '@xt/client/experimental/request/service'
import { EnumApiCourse } from '@xt/client/api'

type userContentEditorOption = {
  type: string
  showBackButton: boolean
  contentRequired: boolean
  imageRequired: boolean
  maxImageSize: number
  editorTitle: string
  uploaderTitle: string
  descriptionTitle: string
  description: string
  isLoading: boolean
  contentPlaceholder: string
  //占位只在提交作业的时候用到
  taskId: number
}

// 阶段数据
export type StageData = {
  homework?: Content // 日记详情-（日记内容等）
  comment?: { commentTotal: number; comments: Array<CommentItem<string>> } // 对应评论内容
  classhourDetail: MockGuideClasshourDetailType // 课时详情
  homeworkSubmit?: userContentEditorOption // 作业数据
  homeworkCard?: WaterfallMoment // 瀑布流卡片数据
}

@BindAll()
export class GuideData {
  constructor() {}

  // 是否通过走完开课前请准备弹窗获取到了一个笔山
  @observable receivedBishan = false

  @observable isGuide: boolean = false

  dataStepOne: DriveStep[] = [
    {
      element: '#stage_one_1',
      popover: {
        description: '在这里，可以查看课程进度和学习有效期。在有效期内，支持下载学习资料及提交作业。',
        progressText: `1 / 10`,
        side: 'bottom'
      }
    },
    {
      element: '#stage_one_2',
      popover: {
        description: '添加书童微信入群后，此处会显示所在的群名称。此外，在这里会展示你的学习进度与作业数据。',
        progressText: `2 / 10`,
        side: 'bottom'
      }
    },
    {
      element: '#stage_one_3',
      popover: {
        description: '这份学习地图清晰呈现出完整的学习旅程全貌，跟随课程进度指引，即可进入相应课时中学习。',
        progressText: `3 / 10`,
        side: 'bottom'
      }
    },
    {
      element: '#stage_one_4',
      popover: {
        description: '在这儿，可以聊聊日常练习，分享学习感悟或是讲讲其他趣事。',
        progressText: `4 / 10`,
        side: 'over',
        align: 'center'
      }
    },
    {
      element: '#stage_one_5',
      popover: {
        description: '桐学们日常与作业的集中展示区。',
        progressText: `5 / 10`,
        side: 'top'
      }
    },
    {
      element: '#stage_one_6',
      popover: {
        description: '桐学们的作业墙，可以在提交作业时，同步分享于此。',
        progressText: `6 / 10`,
        side: 'top'
      }
    },
    {
      element: '#stage_one_7',
      popover: {
        description: '桐学们在此投递自己的笔墨日常。',
        progressText: `7 / 10`,
        side: 'top'
      }
    },
    {
      element: '#stage_one_7v2',
      popover: {
        description: '以一篇内容为例，点击进入并查看详情吧~',
        progressText: `8 / 10`,
        side: 'top',
        align: 'center'
      }
    },
    {
      element: '#stage_one_8',
      popover: {
        description: '遇到与你印心的内容时，可以通过点赞、收藏、评论，留下你的喜爱。',
        progressText: `9 / 10`,
        side: 'top',
        align: 'center'
      }
    },
    {
      popover: {
        description: '现在，以一节课为例，点击进入，学习如何上课吧～',
        progressText: `10 / 10`,
        side: 'left',
        doneBtnText: '下一步'
      }
    },
    {
      element: '#stage_two_1',
      popover: {
        description: '在这里，展示着当前课时的内容预告与上课时间。',
        progressText: `1 / 4`,
        side: 'right'
      }
    },
    {
      element: '#stage_two_2',
      popover: {
        description: '这里可以查看当前课时所要用到的文房文具。',
        progressText: `2 / 4`,
        side: 'left'
      }
    },
    {
      element: '#stage_two_3',
      popover: {
        description: '这里用于下载并保存与本节课相关的学习资料，助力预习、复习及延伸学习。',
        progressText: `3 / 4`,
        side: 'left',
        align: 'start'
      }
    },
    {
      element: '#stage_two_4',
      popover: {
        description: '点击“进入教室”，观看课程。',
        progressText: `4 / 4`,
        side: 'right',
        doneBtnText: '下一步'
      }
    }
  ]
  dataStepTwo: DriveStep[] = [
    {
      element: '#guide-live-stepv2',
      popover: {
        description: '课程为双屏显示，包括老师的实时直播界面和课程讲义界面，方便同时观看。',
        align: 'end',
        showButtons: ['next']
      }
    },
    {
      element: '#guide-live-step1',
      popover: {
        description: '实时直播界面。',
        side: 'bottom'
      }
    },
    {
      element: '#guide-live-stepv2',
      popover: {
        description: '课程讲义界面。',
        side: 'bottom'
      }
    },
    // 第六步调整到第四步的位置
    {
      element: '#guide-live-step4',
      popover: {
        description: '点击 “切换” 按钮，可根据听课需求，在老师的实时直播界面与课程讲义界面之间切换大小屏模式。',
        side: 'left',
        align: 'start'
      }
    },
    {
      element: '#guide-live-step2',
      popover: {
        description: '在聊天区，可在课堂互动时段与老师、桐学们展开实时交流。',
        side: 'right'
      }
    },
    {
      element: '#guide-live-step3',
      popover: {
        description: '如遇播放卡顿，可点击“网络”按钮切换其它线路、或刷新直播页面重新进入。',
        side: 'left',
        align: 'start'
      }
    },
    {
      element: '#guide-live-step5',
      popover: {
        description: '点击 “全屏” 按钮，可全屏查看老师的实时直播界面或课程讲义界面。下面，让我们来观看一段直播画面吧 ~',
        side: 'top',
        align: 'start'
      }
    },
    {
      element: '#guide-live-step1',
      popover: {
        description: '观看直播画面。',
        side: 'left',
        nextBtnText: '跳过'
      }
    },
    {
      popover: {
        description: '课程直播结束后将生成回放，在14天的回放期内可反复观看学习。',
        doneBtnText: '下一步'
      }
    }
  ]
  dataStepThree: DriveStep[] = [
    {
      element: '#stage_two_1',
      popover: {
        description: '回放生成后，此处会显示当前课时回放的剩余观看时间，点击 “进入教室” ，可再次观看课程。',
        progressText: `1 / 6`,
        side: 'right'
      }
    },
    {
      element: '#stage_three_2',
      popover: {
        description: '观看课程后，可在此查看作业要求，进行练习并提交作业。',
        progressText: `2 / 6`,
        side: 'right',
        align: 'center'
      }
    },
    {
      element: '#stage_three_3',
      popover: {
        description: '了解作业内容与要求后，可将作业图片拖拽至此，或点击下方“+”号上传图片。',
        progressText: `3 / 6`,
        side: 'right'
      }
    },
    {
      element: '#stage_three_4',
      popover: {
        description: '可以为作业搭配一段文字说明，同时可将作业设置为公开或私密。若设为公开，发布后作业将在 “发现” 区展示。',
        progressText: `4 / 6`,
        side: 'left',
        align: 'end'
      }
    },
    {
      element: '#stage_three_2',
      popover: {
        description: '点击 “查看” 即可看到已提交作业的详情。待作业完成评阅后，点击“已评阅”，即可查看助教老师的评语。',
        progressText: `5 / 6`,
        side: 'left',
        align: 'center'
      }
    },
    {
      element: '#stage_three_6',
      popover: {
        description: '通过观看课程、提交作业等方式便能获取笔山。它是教室给予桐学们的积分奖励，可用于兑换文房券。',
        progressText: `6 / 6`,
        side: 'left',
        align: 'center',
        doneBtnText: '完成啦'
      }
    }
  ]

  @observable stageOneData: StageData = {
    homework: {
      imageList: [
        {
          waterUrl: `${$global.static}/images/guide/pc/guide-dairy-content-img.jpg`,
          originalUrl: '',
          originalWidth: 320,
          originalHeight: 320
        }
      ],
      memberId: '39236GW0040Z',
      memberNickname: '暄桐书童',
      title: '',
      content: '带着一颗归零的心，认认真真书写。',
      tag: '第1课《零是最好的基础》',
      likeCount: 0,
      createTime: 1751006624175,
      isPublic: false,
      isViolation: 0,
      allowLikeOrCollect: false,
      allowShare: false,
      isLike: false,
      isCollect: false,
      collectCount: 0,
      collectType: 1,
      videoCover: '',
      videoUrl: '',
      contentId: '',
      showReviewedflag: false,
      allowReviewed: false
    },
    comment: {
      commentTotal: 2,
      comments: [
        {
          id: '685e438ca992f673b013b7bb',
          memberId: 'JWPDKZQ0040Z',
          memberNickname: '啊吧啊吧啊吧',
          memberType: 1,
          toCommentId: null,
          content: '写的真好啊。',
          stickersContent: '',
          rootCommentId: '0',
          createTime: 1751008140695,
          like: false,
          likeCount: 1,
          hasMoreChildren: true,
          stickers: [],
          children: []
        },
        {
          id: '685e438ca992f473b013b7bb',
          memberId: 'AT6V2RM0040Z',
          memberNickname: 'Zlll',
          memberType: 1,
          toCommentId: null,
          content: '感受到有力的美。',
          stickersContent: '',
          rootCommentId: '0',
          createTime: 1751008140695,
          like: false,
          likeCount: 1,
          hasMoreChildren: true,
          stickers: [],
          children: []
        }
      ]
    },
    classhourDetail: {
      toolList: [],
      wenfangProductList: [
        {
          id: 1,
          coverUrl: 'https://file.xuantong.cn/picturePath/42bdb0863e5178ea50e53dab7fde267e.png',
          productName: '沉迷神采大羊毫笔',
          alias: '',
          pageUrl: ''
        },
        {
          id: 2,
          coverUrl: 'https://file.xuantong.cn/picturePath/433b8bf42931aed2af47125268c111eb.png',
          productName: '笔墨精进大羊毫笔',
          alias: '',
          pageUrl: ''
        },
        {
          id: 6,
          coverUrl: 'https://file.xuantong.cn/picturePath/00fe0995011e1900ba37a8b2a21970e5.png',
          productName: '毛笔套装',
          alias: '',
          pageUrl: ''
        },
        {
          id: 7,
          coverUrl: 'https://file.xuantong.cn/picturePath/2423c647ee71664cf5e424f4e10d1a47.png',
          productName: '划沙纸（A款）',
          alias: '',
          pageUrl: ''
        },
        {
          id: 8,
          coverUrl: 'https://file.xuantong.cn/picturePath/9ebc3b470f6288e4c1803e567e961a6a.png',
          productName: '拨云纸（B款）',
          alias: '',
          pageUrl: ''
        },
        {
          id: 11,
          coverUrl: 'https://file.xuantong.cn/picturePath/3a8d56ce41c4f12ad2352bcac9914172.png',
          productName: '清供墨液',
          alias: '',
          pageUrl: ''
        },
        {
          id: 12,
          coverUrl: 'https://file.xuantong.cn/picturePath/315e9d5fec3e96aea150ec3a036bf785.png',
          productName: '黛檀墨液',
          alias: '',
          pageUrl: ''
        },
        {
          id: 13,
          coverUrl: 'https://file.xuantong.cn/picturePath/235b09f6c45351cb8ac6c0958202c847.png',
          productName: '墨碟',
          alias: '',
          pageUrl: ''
        },
        {
          id: 14,
          coverUrl: 'https://file.xuantong.cn/picturePath/005e63d88e65db07babc1aa474a22ceb.png',
          productName: '学习包字帖',
          alias: '',
          pageUrl: ''
        },
        {
          id: 15,
          coverUrl: 'https://file.xuantong.cn/picturePath/b308f1614b40c96da72cea47573a99e0.png',
          productName: '纸镇',
          alias: '',
          pageUrl: ''
        }
      ],
      homeworkTaskList: [
        {
          id: 1035,
          title: '写字作业',
          reviewed: null,
          homeworkId: null,
          homeworkType: 3,
          homeworkBizType: 1,
          submittedHomework: false,
          publishStatus: 1,
          memberHomeworks: null
        },
        {
          id: 1036,
          title: '阅读作业',
          reviewed: null,
          homeworkId: null,
          homeworkType: 1,
          homeworkBizType: 2,
          submittedHomework: false,
          publishStatus: 1,
          memberHomeworks: null
        }
      ],
      materialList: [
        {
          id: 301,
          name: '【001】写字旅行攻略第01节-暄桐的愿心.pdf',
          filePath: 'files/9e5837f89a95d93d25aafaba2761628f.pptx',
          size: 17445265,
          url: ''
        },
        {
          id: 302,
          name: '【【002】写字旅行攻略第01节-暄桐校歌|笔墨是小舟.mp3',
          filePath: 'files/9e5837f89a95d93d25aafaba2761628f.pptx',
          size: 17445265,
          url: ''
        },
        {
          id: 303,
          name: '【003】写字旅行攻略第01节-文房养护小贴士.pdf',
          filePath: 'files/9e5837f89a95d93d25aafaba2761628f.pptx',
          size: 17445265,
          url: ''
        }
      ],
      classhourInfo: {
        id: 3770,
        classhourType: 1,
        courseId: 478,
        name: '第1课 零是最好的基础',
        subName: '书写姿势与线条练习',
        content:
          '<p>这堂课我们将正式开启学习之旅，首先明晰课程的核心与方向，继而探讨关于“戒定慧”与“知行合一”，以及零基础与基本功之间的关系。</p><p>与此同时，我们还将学习正确的书写姿势，而用笔基础的训练，便从写下第一根线条开始。</p>',
        coverUrl: 'https://static.xuantong.cn/course/class-live.png',
        liveCoverUrl: `${$global.static}/images/guide/pc/guide-cover-img.jpg`,
        liveBackgroundCoverUrl: null,
        publishStatus: 1,
        startTime: 1741491315000,
        liveEnd: true,
        playbackStatus: 0,
        playbackGeneratedTime: null,
        playbackEndTime: null,
        studyBuffEndTime: null,
        defaultPlaybackDuration: 1,
        videoPlay: false,
        publishTime: 0,
        materialsStorageUrl: null
      }
    },
    homeworkCard: {
      id: '68874c820d2f292336107efd',
      like: false,
      likeCount: 0,
      memberId: '39236GW0040Z',
      memberNickname: '暄桐书童',
      pictures: [
        {
          waterUrl: `${$global.static}/images/guide/pc/guide-dairy-content-img.jpg`,
          originWidth: 320,
          originHeight: 320,
          isViolation: 0
        }
      ],
      sourceId: 478,
      sourceType: 3,
      bizId: 533970,
      title: '',
      top: false,
      isViolation: false,
      content: '带着一颗归零的心，认认真真书写。',
      createTime: 1753697410258,
      sourceDescription: '第1课《零是最好的基础》'
    }
  }
  stageThreeDataOld: StageData = {
    classhourDetail: {
      toolList: [],
      wenfangProductList: [
        {
          id: 1,
          coverUrl: 'https://file.xuantong.cn/picturePath/42bdb0863e5178ea50e53dab7fde267e.png',
          productName: '沉迷神采大羊毫笔',
          alias: '',
          pageUrl: ''
        },
        {
          id: 2,
          coverUrl: 'https://file.xuantong.cn/picturePath/433b8bf42931aed2af47125268c111eb.png',
          productName: '笔墨精进大羊毫笔',
          alias: '',
          pageUrl: ''
        },
        {
          id: 6,
          coverUrl: 'https://file.xuantong.cn/picturePath/00fe0995011e1900ba37a8b2a21970e5.png',
          productName: '毛笔套装',
          alias: '',
          pageUrl: ''
        },
        {
          id: 7,
          coverUrl: 'https://file.xuantong.cn/picturePath/2423c647ee71664cf5e424f4e10d1a47.png',
          productName: '划沙纸（A款）',
          alias: '',
          pageUrl: ''
        },
        {
          id: 8,
          coverUrl: 'https://file.xuantong.cn/picturePath/9ebc3b470f6288e4c1803e567e961a6a.png',
          productName: '拨云纸（B款）',
          alias: '',
          pageUrl: ''
        },
        {
          id: 11,
          coverUrl: 'https://file.xuantong.cn/picturePath/3a8d56ce41c4f12ad2352bcac9914172.png',
          productName: '清供墨液',
          alias: '',
          pageUrl: ''
        },
        {
          id: 12,
          coverUrl: 'https://file.xuantong.cn/picturePath/315e9d5fec3e96aea150ec3a036bf785.png',
          productName: '黛檀墨液',
          alias: '',
          pageUrl: ''
        },
        {
          id: 13,
          coverUrl: 'https://file.xuantong.cn/picturePath/235b09f6c45351cb8ac6c0958202c847.png',
          productName: '墨碟',
          alias: '',
          pageUrl: ''
        },
        {
          id: 14,
          coverUrl: 'https://file.xuantong.cn/picturePath/005e63d88e65db07babc1aa474a22ceb.png',
          productName: '学习包字帖',
          alias: '',
          pageUrl: ''
        },
        {
          id: 15,
          coverUrl: 'https://file.xuantong.cn/picturePath/b308f1614b40c96da72cea47573a99e0.png',
          productName: '纸镇',
          alias: '',
          pageUrl: ''
        }
      ],
      homeworkTaskList: [
        {
          id: 1035,
          title: '写字作业',
          reviewed: null,
          homeworkId: null,
          homeworkType: 3,
          homeworkBizType: 1,
          submittedHomework: false,
          publishStatus: 1,
          memberHomeworks: null
        },
        {
          id: 1036,
          title: '阅读作业',
          reviewed: null,
          homeworkId: null,
          homeworkType: 1,
          homeworkBizType: 2,
          submittedHomework: false,
          publishStatus: 1,
          memberHomeworks: null
        }
      ],
      materialList: [
        {
          id: 301,
          name: '【001】写字旅行攻略第01节-暄桐的愿心.pdf',
          filePath: 'files/9e5837f89a95d93d25aafaba2761628f.pptx',
          size: 17445265,
          url: ''
        },
        {
          id: 302,
          name: '【【002】写字旅行攻略第01节-暄桐校歌|笔墨是小舟.mp3',
          filePath: 'files/9e5837f89a95d93d25aafaba2761628f.pptx',
          size: 17445265,
          url: ''
        },
        {
          id: 303,
          name: '【003】写字旅行攻略第01节-文房养护小贴士.pdf',
          filePath: 'files/9e5837f89a95d93d25aafaba2761628f.pptx',
          size: 17445265,
          url: ''
        }
      ],
      classhourInfo: {
        id: 3770,
        classhourType: 1,
        courseId: 478,
        name: '第1课 零是最好的基础',
        subName: '书写姿势与线条练习',
        content:
          '<p>这堂课我们将正式开启学习之旅，首先明晰课程的核心与方向，继而探讨关于“戒定慧”与“知行合一”，以及零基础与基本功之间的关系。</p><p>与此同时，我们还将学习正确的书写姿势，而用笔基础的训练，便从写下第一根线条开始。</p>',
        coverUrl: 'https://static.xuantong.cn/course/class-live.png',
        liveCoverUrl: `${$global.static}/images/guide/pc/guide-cover-img.jpg`,
        liveBackgroundCoverUrl: null,
        publishStatus: 1,
        startTime: 1741491315000,
        liveEnd: true,
        playbackStatus: 1,
        playbackGeneratedTime: null,
        playbackEndTime: null,
        studyBuffEndTime: null,
        defaultPlaybackDuration: 1,
        videoPlay: false,
        publishTime: 0,
        materialsStorageUrl: null
      }
    },
    homeworkSubmit: {
      type: 'homework',
      showBackButton: true,
      contentRequired: false,
      imageRequired: true,
      maxImageSize: 9,
      editorTitle: '作业内容',
      uploaderTitle: '作业图片',
      descriptionTitle: '作业详情',
      description:
        '<h3 style="text-align:center;"><span style="color:#07767a;"><strong>作业内容</strong></span></h3><p style="text-align:center;"><span style="color:hsl(0, 0%, 30%);">体会正确的执笔姿势，试着蘸墨画下一条完整的线，每天保持10-20条线的练习量就很足够。</span></p><p style="text-align:center;">&nbsp;</p><h3 style="text-align:center;"><span style="color:#07767a;"><strong>请努力做到</strong></span></h3><p style="text-align:center;"><span style="color:hsl(0, 0%, 30%);">整篇作业干净整洁，每一张练习都保持作品感。</span></p><p style="text-align:center;">&nbsp;</p><h3 style="text-align:center;"><span style="color:#07767a;"><strong>作业方法</strong></span></h3><p style="text-align:center;"><span style="color:hsl(0, 0%, 30%);">这是我们的第一次作业，每天的练习数量并非最重要的，而是试着在过程里，体会肩、颈、肘、腕是否处在比较放松舒适的状态。</span></p><p style="text-align:center;">&nbsp;</p><h3 style="text-align:center;"><span style="color:#07767a;"><strong>作业锦囊</strong></span></h3><p style="text-align:center;"><span style="color:hsl(0, 0%, 30%);">放轻松，行笔时气息需稳定。最重要的还是一颗专注稳定的心，加油。</span></p>',
      isLoading: false,
      contentPlaceholder: '在完成作业的过程中，磨手练心，积累功夫，这便是实实在在的进步。',
      taskId: 1036
    }
  }
  stageThreeDataNew: StageData = {
    classhourDetail: {
      toolList: [],
      wenfangProductList: [
        {
          id: 1,
          coverUrl: 'https://file.xuantong.cn/picturePath/42bdb0863e5178ea50e53dab7fde267e.png',
          productName: '沉迷神采大羊毫笔',
          alias: '',
          pageUrl: ''
        },
        {
          id: 2,
          coverUrl: 'https://file.xuantong.cn/picturePath/433b8bf42931aed2af47125268c111eb.png',
          productName: '笔墨精进大羊毫笔',
          alias: '',
          pageUrl: ''
        },
        {
          id: 6,
          coverUrl: 'https://file.xuantong.cn/picturePath/00fe0995011e1900ba37a8b2a21970e5.png',
          productName: '毛笔套装',
          alias: '',
          pageUrl: ''
        },
        {
          id: 7,
          coverUrl: 'https://file.xuantong.cn/picturePath/2423c647ee71664cf5e424f4e10d1a47.png',
          productName: '划沙纸（A款）',
          alias: '',
          pageUrl: ''
        },
        {
          id: 8,
          coverUrl: 'https://file.xuantong.cn/picturePath/9ebc3b470f6288e4c1803e567e961a6a.png',
          productName: '拨云纸（B款）',
          alias: '',
          pageUrl: ''
        },
        {
          id: 11,
          coverUrl: 'https://file.xuantong.cn/picturePath/3a8d56ce41c4f12ad2352bcac9914172.png',
          productName: '清供墨液',
          alias: '',
          pageUrl: ''
        },
        {
          id: 12,
          coverUrl: 'https://file.xuantong.cn/picturePath/315e9d5fec3e96aea150ec3a036bf785.png',
          productName: '黛檀墨液',
          alias: '',
          pageUrl: ''
        },
        {
          id: 13,
          coverUrl: 'https://file.xuantong.cn/picturePath/235b09f6c45351cb8ac6c0958202c847.png',
          productName: '墨碟',
          alias: '',
          pageUrl: ''
        },
        {
          id: 14,
          coverUrl: 'https://file.xuantong.cn/picturePath/005e63d88e65db07babc1aa474a22ceb.png',
          productName: '学习包字帖',
          alias: '',
          pageUrl: ''
        },
        {
          id: 15,
          coverUrl: 'https://file.xuantong.cn/picturePath/b308f1614b40c96da72cea47573a99e0.png',
          productName: '纸镇',
          alias: '',
          pageUrl: ''
        }
      ],
      homeworkTaskList: [
        {
          id: 1035,
          title: '写字作业',
          reviewed: null,
          homeworkId: null,
          homeworkType: 3,
          homeworkBizType: 1,
          submittedHomework: true,
          publishStatus: 1,
          memberHomeworks: [
            {
              id: 525631,
              memberNickname: '俞晨测试账号测试账号测试账号啊啊啊啊啊啊啧啧啧啧啧',
              content: '带着一颗归零的心，认认真真书写',
              title: '',
              memberId: 904695428035907615,
              reviewed: false,
              reviewerId: 0,
              reviewerNickname: '',
              homeworkLevel: 0,
              reviewedFlag: 0,
              createTime: 1750239011000,
              updateTime: 1750239012000,
              classhourId: 3770,
              homeworkPicList: [
                {
                  id: 1884864,
                  homeworkId: 525634,
                  originalUrl: 'https://testing-file.xuantong.cn/images/origin/af289b351bcfa1a0b4cee3d1ed120297.jpg',
                  waterUrl: `${$global.static}/images/guide/pc/guide-dairy-content-img.jpg`,
                  createTime: 1750239011000,
                  updateTime: 1750239012000,
                  likerId: 0,
                  valid: true
                }
              ]
            }
          ]
        },
        {
          id: 1036,
          title: '阅读作业',
          reviewed: null,
          homeworkId: null,
          homeworkType: 1,
          homeworkBizType: 2,
          submittedHomework: true,
          publishStatus: 1,
          memberHomeworks: [
            {
              id: 5256431,
              memberNickname: '俞晨测试账号测试账号测试账号啊啊啊啊啊啊啧啧啧啧啧',
              content: '好好写字，等待果子自然成熟的那一天。《日日是好日》教会我要珍惜每一次相聚的时光，好好享受每一天。',
              title: '',
              memberId: 904695428035907615,
              reviewed: true,
              reviewerId: 0,
              reviewerNickname: '怀有助教',
              homeworkLevel: 0,
              createTime: 1750239011000,
              updateTime: 1750239012000,
              reviewedFlag: 0,
              classhourId: 3770,
              homeworkPicList: [
                {
                  id: 1883864,
                  homeworkId: 525634,
                  originalUrl: 'https://testing-file.xuantong.cn/images/origin/af289b351bcfa1a0b4cee3d1ed120297.jpg',
                  waterUrl: `${$global.static}/images/guide/pc/guide-dairy-content-img2.jpg`,
                  createTime: 1750239011000,
                  updateTime: 1750239012000,
                  likerId: 0,
                  valid: true
                }
              ]
            }
          ]
        }
      ],
      materialList: [
        {
          id: 301,
          name: '【001】写字旅行攻略第01节-暄桐的愿心.pdf',
          filePath: 'files/9e5837f89a95d93d25aafaba2761628f.pptx',
          size: 17445265,
          url: ''
        },
        {
          id: 302,
          name: '【【002】写字旅行攻略第01节-暄桐校歌|笔墨是小舟.mp3',
          filePath: 'files/9e5837f89a95d93d25aafaba2761628f.pptx',
          size: 17445265,
          url: ''
        },
        {
          id: 303,
          name: '【003】写字旅行攻略第01节-文房养护小贴士.pdf',
          filePath: 'files/9e5837f89a95d93d25aafaba2761628f.pptx',
          size: 17445265,
          url: ''
        }
      ],
      classhourInfo: {
        id: 3770,
        classhourType: 1,
        courseId: 478,
        name: '第1课 零是最好的基础',
        subName: '书写姿势与线条练习',
        content:
          '<p>这堂课我们将正式开启学习之旅，首先明晰课程的核心与方向，继而探讨关于“戒定慧”与“知行合一”，以及零基础与基本功之间的关系。</p><p>与此同时，我们还将学习正确的书写姿势，而用笔基础的训练，便从写下第一根线条开始。</p>',
        coverUrl: 'https://static.xuantong.cn/course/class-live.png',
        liveCoverUrl: `${$global.static}/images/guide/pc/guide-cover-img.jpg`,
        liveBackgroundCoverUrl: null,
        publishStatus: 1,
        startTime: 1741491315000,
        liveEnd: true,
        playbackStatus: 1,
        playbackGeneratedTime: null,
        playbackEndTime: null,
        studyBuffEndTime: null,
        defaultPlaybackDuration: 1,
        videoPlay: false,
        publishTime: 0,
        materialsStorageUrl: null
      }
    },
    homeworkSubmit: {
      type: 'homework',
      showBackButton: true,
      contentRequired: false,
      imageRequired: true,
      maxImageSize: 9,
      editorTitle: '作业内容',
      uploaderTitle: '作业图片',
      descriptionTitle: '作业详情',
      description:
        '<h3 style="text-align:center;"><span style="color:#07767a;"><strong>作业内容</strong></span></h3><p style="text-align:center;"><span style="color:hsl(0, 0%, 30%);">体会正确的执笔姿势，试着蘸墨画下一条完整的线，每天保持10-20条线的练习量就很足够。</span></p><p style="text-align:center;">&nbsp;</p><h3 style="text-align:center;"><span style="color:#07767a;"><strong>请努力做到</strong></span></h3><p style="text-align:center;"><span style="color:hsl(0, 0%, 30%);">整篇作业干净整洁，每一张练习都保持作品感。</span></p><p style="text-align:center;">&nbsp;</p><h3 style="text-align:center;"><span style="color:#07767a;"><strong>作业方法</strong></span></h3><p style="text-align:center;"><span style="color:hsl(0, 0%, 30%);">这是我们的第一次作业，每天的练习数量并非最重要的，而是试着在过程里，体会肩、颈、肘、腕是否处在比较放松舒适的状态。</span></p><p style="text-align:center;">&nbsp;</p><h3 style="text-align:center;"><span style="color:#07767a;"><strong>作业锦囊</strong></span></h3><p style="text-align:center;"><span style="color:hsl(0, 0%, 30%);">放轻松，行笔时气息需稳定。最重要的还是一颗专注稳定的心，加油。</span></p>',
      isLoading: false,
      contentPlaceholder: '在完成作业的过程中，磨手练心，积累功夫，这便是实实在在的进步。',
      taskId: 1036
    }
  }

  @action
  setGuideStatus(isActive: boolean) {
    this.isGuide = isActive
  }

  @action
  updateReceivedBishan(isReceived: boolean) {
    this.receivedBishan = isReceived
  }

  async onCheckReceivedBishan(courseId: string) {
    const [, success] = await $service<boolean>({
      method: 'POST',
      url: EnumApiCourse.GetBiShan,
      data: { id: courseId }
    })

    if (success === true) {
      this.updateReceivedBishan(true)
    }

    return success === true
  }

  @action
  getStageOneData(): StageData {
    const date = moment(Date.now())
    // 开始时间-决定status
    const startTime = date.add(13, 'days').add(6, 'h').add(28, 'm').valueOf()
    // 更新开始时间
    this.stageOneData.classhourDetail.classhourInfo.startTime = startTime

    return this.stageOneData
  }

  @action
  setLiveTimeChange(): void {
    const date = moment(Date.now())
    // 开始时间-决定status
    const startTime = date.add(3, 'h').add(28, 'm').valueOf()
    // 更新开始时间
    this.stageOneData.classhourDetail.classhourInfo.startTime = startTime
  }

  // true-返回老数据，false-返回新数据
  getStageThreeData(flag: boolean): StageData {
    const date = moment(Date.now())
    // 回放时间-决定status
    const playbackEndTime = date.add(13, 'days').add(8, 'h').add(38, 'm').valueOf()
    // 更新回放时间
    if (flag) {
      this.stageThreeDataOld.classhourDetail.classhourInfo.playbackEndTime = playbackEndTime
    } else {
      this.stageThreeDataNew.classhourDetail.classhourInfo.playbackEndTime = playbackEndTime
    }
    return flag ? this.stageThreeDataOld : this.stageThreeDataNew
  }
}
